import type { SupervisionStatus } from '../../../common/enums/supervisionStatus';
import { CustomControlStatus } from '../../../common/enums/customControlStatus';
import { ProcedureStatus } from '../../../common/enums/procedureStatus';
import { ReportCategories } from '../../../common/enums/reportCategories';
import { ReportStatus } from '../../../common/enums/reportStatus';
import { RequestStatus } from '../../../common/enums/requestStatus';
import { ServiceCode } from '../../../common/enums/serviceCode';
import { ServiceType } from '../../../common/enums/serviceType';
import { StatusLabelStatus } from '../../../common/enums/statusLabelStatus';
import { TaskStatus, TaskType } from '../../../common/enums/task';

export interface IRequest {
    code?: ServiceCode;
    diagnosticReports?: IDiagnosticReport[];
    procedure?: {
        customControlStatus?: CustomControlStatus | null;
        status: ProcedureStatus;
    };
    service?: {
        code: ServiceCode;
        serviceType: ServiceType;
    };
    serviceType?: ServiceType;
    status: RequestStatus;
    supervision?: {
        status: SupervisionStatus;
    };
    tasks?: ITask[];
}

interface ITask {
    status: TaskStatus;
    type: TaskType;
}

interface IDiagnosticReport {
    category?: ReportCategories;
    clarificationQAPerformerSentDateTime?: string | null;
    clarificationQARequesterSentDateTime?: string | null;
    reviewStatus: ReportStatus;
}

export function getStatusLabel(request: IRequest) {
    const serviceType = request.serviceType || (request.service && request.service.serviceType);
    const code = request.code || (request.service && request.service.code);

    if (serviceType === ServiceType.examination) {
        if (
            isRequestStatus(RequestStatus.completed) &&
            isProcedureStatus(ProcedureStatus.completed) &&
            isReportStatus(ReportStatus.closed) &&
            isSupervisionStatus(TaskStatus.closed)
        ) {
            return StatusLabelStatus.supervised;
        }
        if (
            isRequestStatus(RequestStatus.completed) &&
            isProcedureStatus(ProcedureStatus.completed) &&
            isReportStatus(ReportStatus.closed) &&
            (isSupervisionStatus(TaskStatus.accepted) || isSupervisionStatus(TaskStatus.open))
        ) {
            return StatusLabelStatus.withSupervisor;
        }
        if (
            isRequestStatus(RequestStatus.active) &&
            isProcedureStatus(ProcedureStatus.completed) &&
            isReportStatus(ReportStatus.open)
        ) {
            return StatusLabelStatus.completed;
        }
        if (
            isRequestStatus(RequestStatus.active) &&
            isProcedureStatus(ProcedureStatus.completed) &&
            isReportStatus(ReportStatus.draft)
        ) {
            return StatusLabelStatus.withGrader;
        }
        if (
            isRequestStatus(RequestStatus.active) &&
            isProcedureStatus(ProcedureStatus.inProgress) &&
            isReportStatus(ReportStatus.draft)
        ) {
            return StatusLabelStatus.withBiometrician;
        }
        if (
            isRequestStatus(RequestStatus.completed) &&
            isProcedureStatus(ProcedureStatus.completed) &&
            isReportStatus(ReportStatus.closed)
        ) {
            return StatusLabelStatus.closed;
        }
        if (isRequestStatus(RequestStatus.active) && isProcedureStatus(ProcedureStatus.completed)) {
            return StatusLabelStatus.withGrader;
        }
        if (
            isRequestStatus(RequestStatus.accepted) ||
            (isRequestStatus(RequestStatus.active) &&
                (!request.procedure ||
                    (isProcedureStatus(ProcedureStatus.inProgress) && !isReportStatus(ReportStatus.draft))))
        ) {
            return StatusLabelStatus.withBiometrician;
        }
        if (
            isRequestStatus(RequestStatus.active) &&
            isProcedureStatus(ProcedureStatus.active) &&
            request.procedure &&
            isProcedureCustomControlStatus(CustomControlStatus.withPatient)
        ) {
            return StatusLabelStatus.withPatient;
        }
        if (
            isRequestStatus(RequestStatus.active) &&
            isProcedureStatus(ProcedureStatus.active) &&
            isProcedureCustomControlStatus(CustomControlStatus.uploading)
        ) {
            return StatusLabelStatus.dataTransferred;
        }
        if (
            isRequestStatus(RequestStatus.active) &&
            isProcedureStatus(ProcedureStatus.active) &&
            isProcedureCustomControlStatus(CustomControlStatus.assetNeeded)
        ) {
            return StatusLabelStatus.assetNeeded;
        }
        if (
            isRequestStatus(RequestStatus.active) &&
            isProcedureStatus(ProcedureStatus.active) &&
            isProcedureCustomControlStatus(CustomControlStatus.analysis)
        ) {
            return StatusLabelStatus.withAnalysis;
        }
        if (isRequestStatus(RequestStatus.requested) && !request.procedure) {
            if (code === ServiceCode.OSASQ) {
                return StatusLabelStatus.notYetSent;
            } else {
                return StatusLabelStatus.requested;
            }
        }
        if (isRequestStatus(RequestStatus.requested)) {
            if (code === ServiceCode.OSASQ) {
                return StatusLabelStatus.notYetSent;
            } else {
                return StatusLabelStatus.requested;
            }
        }
        if (isRequestStatus(RequestStatus.cancelled)) {
            return StatusLabelStatus.cancelled;
        }
        if (isRequestStatus(RequestStatus.draft)) {
            return StatusLabelStatus.draftRequest;
        }
        if (isRequestStatus(RequestStatus.aborted)) {
            return StatusLabelStatus.aborted;
        }
        if (isRequestStatus(RequestStatus.withAdmin)) {
            return StatusLabelStatus.withAdmin;
        }
    }

    if (serviceType === ServiceType.consultation) {
        if (isRequestStatus(RequestStatus.aborted)) {
            return StatusLabelStatus.aborted;
        }
        if (isRequestStatus(RequestStatus.completed) && isReportStatus(ReportStatus.closed)) {
            return StatusLabelStatus.closed;
        }
        if (isRequestStatus(RequestStatus.active) && isReportStatus(ReportStatus.open)) {
            if (isQAPerformer()) {
                return StatusLabelStatus.answeredSecondRound;
            }
            if (isQARequester()) {
                return StatusLabelStatus.sentSecondRound;
            }

            return StatusLabelStatus.answeredFirstRound;
        }
        if (
            isRequestStatus(RequestStatus.active) &&
            (!request.diagnosticReports?.[0] || isReportStatus(ReportStatus.draft)) // Sometimes the draft is visible, sometimes it's not
        ) {
            if (code === ServiceCode.OSASC1 || code === ServiceCode.OSASC2) {
                return StatusLabelStatus.newContact;
            } else {
                return StatusLabelStatus.sentFirstRound;
            }
        }
        if (isRequestStatus(RequestStatus.accepted)) {
            if (code === ServiceCode.OSASC1) {
                return StatusLabelStatus.newContact;
            } else {
                return StatusLabelStatus.sentFirstRound;
            }
        }
        if (isRequestStatus(RequestStatus.draft)) {
            return StatusLabelStatus.draftConsult;
        }
        if (isRequestStatus(RequestStatus.cancelled)) {
            return StatusLabelStatus.cancelled;
        }
        if (isRequestStatus(RequestStatus.requested)) {
            if (code === ServiceCode.OSASC1 || code === ServiceCode.OSASC2) {
                return StatusLabelStatus.newContact;
            } else {
                return StatusLabelStatus.sentFirstRound;
            }
        }
    }

    if (serviceType === ServiceType.referral || serviceType === ServiceType.referralSmall) {
        if (isRequestStatus(RequestStatus.draft)) {
            return StatusLabelStatus.draftReferral;
        }
        if (isRequestStatus(RequestStatus.completed)) {
            return StatusLabelStatus.closed;
        }
        if (isRequestStatus(RequestStatus.requested)) {
            return StatusLabelStatus.sent;
        }
        if (isRequestStatus(RequestStatus.accepted)) {
            return StatusLabelStatus.accepted;
        }
        if (isRequestStatus(RequestStatus.cancelled)) {
            return StatusLabelStatus.cancelled;
        }
        if (isRequestStatus(RequestStatus.aborted)) {
            return StatusLabelStatus.aborted;
        }
        if (isRequestStatus(RequestStatus.rejected)) {
            return StatusLabelStatus.rejected;
        }
        if (isRequestStatus(RequestStatus.rejectedAndResent)) {
            return StatusLabelStatus.rejectedAndResent;
        }
        if (isRequestStatus(RequestStatus.rejectedAndArchived)) {
            return StatusLabelStatus.rejectedAndArchived;
        }
        if (isRequestStatus(RequestStatus.active)) {
            return statusReferralIntakeAndDischarge();
        }
    }

    if (serviceType === ServiceType.contact) {
        if (isRequestStatus(RequestStatus.draft)) {
            return StatusLabelStatus.draftContact;
        }
        if (isRequestStatus(RequestStatus.completed)) {
            return StatusLabelStatus.sent;
        }
        if (isRequestStatus(RequestStatus.cancelled)) {
            return StatusLabelStatus.cancelled;
        }
        if (isRequestStatus(RequestStatus.aborted)) {
            return StatusLabelStatus.aborted;
        }
    }

    if (serviceType === ServiceType.counseling) {
        if (isRequestStatus(RequestStatus.draft)) {
            return StatusLabelStatus.draftCounseling;
        }
        if (isRequestStatus(RequestStatus.accepted) || isRequestStatus(RequestStatus.requested)) {
            return StatusLabelStatus.invited;
        }
        if (isRequestStatus(RequestStatus.active)) {
            return StatusLabelStatus.started;
        }
        if (isRequestStatus(RequestStatus.cancelled)) {
            return StatusLabelStatus.cancelled;
        }
        if (isRequestStatus(RequestStatus.completed)) {
            return StatusLabelStatus.closed;
        }
    }

    if (serviceType === ServiceType.invitation) {
        if (isRequestStatus(RequestStatus.cancelled)) {
            return StatusLabelStatus.cancelled;
        }
        if (isRequestStatus(RequestStatus.aborted)) {
            return StatusLabelStatus.aborted;
        }
        if (isProcedureStatus(ProcedureStatus.active)) {
            if (isProcedureCustomControlStatus(CustomControlStatus.withPatient)) {
                return StatusLabelStatus.started;
            } else {
                return StatusLabelStatus.invited;
            }
        }
        if (isProcedureStatus(ProcedureStatus.completed)) {
            return StatusLabelStatus.closed;
        }
    }

    return undefined;

    function isRequestStatus(status: RequestStatus) {
        if (request.status === status) {
            return true;
        }

        return false;
    }

    function isProcedureStatus(status: ProcedureStatus) {
        if (request.procedure && request.procedure.status === status) {
            return true;
        }

        return false;
    }

    function isReportStatus(status: ReportStatus) {
        if (request.diagnosticReports?.[0]?.reviewStatus === status) {
            return true;
        }

        return false;
    }

    function isSupervisionStatus(status: TaskStatus) {
        return !!request.tasks?.some((task) => task.type === TaskType.supervise && task.status === status);
    }

    function isProcedureCustomControlStatus(status: CustomControlStatus) {
        if (request.procedure && request.procedure.customControlStatus === status) {
            return true;
        }

        return false;
    }

    function isQARequester() {
        if (request.diagnosticReports && request.diagnosticReports[0].clarificationQARequesterSentDateTime) {
            return true;
        }

        return false;
    }

    function isQAPerformer() {
        if (request.diagnosticReports && request.diagnosticReports[0].clarificationQAPerformerSentDateTime) {
            return true;
        }

        return false;
    }

    function statusReferralIntakeAndDischarge() {
        if (request.diagnosticReports && request.diagnosticReports.length > 0) {
            const intakeReport = request.diagnosticReports.find((dp) => dp.category === ReportCategories.intake);
            const dischargeReport = request.diagnosticReports.find((dp) => dp.category === ReportCategories.discharge);

            if (intakeReport && dischargeReport) {
                switch (dischargeReport.reviewStatus) {
                    case ReportStatus.draft:
                        return StatusLabelStatus.dischargeDraft;
                    case ReportStatus.open:
                        return StatusLabelStatus.dischargeCompleted;
                    case ReportStatus.closed:
                        return StatusLabelStatus.closed;
                    default:
                        return undefined;
                }
            } else if (intakeReport) {
                switch (intakeReport.reviewStatus) {
                    case ReportStatus.draft:
                        return StatusLabelStatus.intakeDraft;
                    case ReportStatus.open:
                        return StatusLabelStatus.intakeCompleted;
                    case ReportStatus.closed:
                        return StatusLabelStatus.intakeCompletedAdmin;
                    default:
                        return undefined;
                }
            } else if (dischargeReport) {
                switch (dischargeReport.reviewStatus) {
                    case ReportStatus.draft:
                        return StatusLabelStatus.dischargeDraft;
                    case ReportStatus.open:
                        return StatusLabelStatus.dischargeCompleted;
                    case ReportStatus.closed:
                        return StatusLabelStatus.closed;
                    default:
                        return undefined;
                }
            }
        }
    }
}
